import { useCallback, useMemo } from "react";

import { useAppSelector } from "app/hooks";
import { PRE_AUTH_USER_ID, selectCurrentUser } from "features/auth/auth-slice";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import type {
  HeaderChoicesByScope,
  HeaderScope,
  UserHeaderChoice
} from "./patient-header-slice";

/**
 * Convention is `suno_<feature>_v<N>_` + user id. Bump `v2_` to reset every user.
 *
 * `v1_` held a bare `"collapsed"`/`"expanded"` string — one global choice for the whole chart.
 * `v2_` holds a record keyed by `HeaderScope`. The version moved rather than the shape being
 * detected in place because a v1 value cannot say which scope it was about, so there is nothing to
 * migrate; the orphaned v1 key is inert.
 *
 * The user-id suffix is load-bearing, not decorative: logout does NOT clear this key.
 * `teardownClientSession` (`auth-slice.ts`) calls only `clearAuthStorage()`, and the blanket
 * `clearNonAuthLocalStorage` is wired to nothing but the manual "Clear app data" button. On a
 * shared clinic front-desk workstation an unsuffixed key would hand the next user whatever the
 * last one chose.
 */
export const HEADER_COLLAPSE_STORAGE_KEY = "suno_patient_header_collapsed_v2_";

const isUserHeaderChoice = (value: unknown): value is UserHeaderChoice =>
  value === "collapsed" || value === "expanded";

/**
 * Anything that is not a record of known scopes to known choices reads as "no preference" — a
 * value left by an earlier shape of this feature (the v1 bare string, say) is truthy, and callers
 * treat any stored choice as deliberate, which would wedge the header past every automatic rule.
 *
 * An empty record passes, which is exactly right for "read, nothing chosen yet". An array fails on
 * its numeric keys, so it needs no check of its own.
 */
const isHeaderChoicesByScope = (
  value: unknown
): value is HeaderChoicesByScope =>
  typeof value === "object" &&
  value !== null &&
  Object.entries(value).every(
    ([key, choice]) =>
      (key === "e-documents" || key === "other") && isUserHeaderChoice(choice)
  );

/**
 * localStorage side of the patient header's collapse preference. Owns the key and nothing else:
 * Redux holds the live value (`patient-header-slice`), and `PatientHeaderProvider` is the one
 * place that connects the two.
 *
 * Storage is `null` until a REAL user id resolves. `RequireAuth` gates on the token alone, so
 * the app renders while `/auth/users/me/` is still in flight — and during that window
 * `auth.user.id` is not absent, it is the `PRE_AUTH_USER_ID` placeholder (`-1`). A truthiness
 * check therefore passes and quietly builds a `..._-1` bucket shared by every user of the
 * machine, which is the exact failure the suffix exists to prevent. Compare against the
 * sentinel, the way `protected-route.component.tsx` and `routes.tsx` already do.
 */
export default function usePatientHeaderCollapsePreference() {
  const currentUser = useAppSelector(selectCurrentUser);
  const userId = currentUser?.id;
  const isAuthenticatedUser =
    typeof userId === "number" && userId !== PRE_AUTH_USER_ID && userId > 0;

  const storage = useMemo(
    () =>
      isAuthenticatedUser
        ? setupLocalStorageAPI(`${HEADER_COLLAPSE_STORAGE_KEY}${userId}`)
        : null,
    [isAuthenticatedUser, userId]
  );

  /**
   * `{}` is the fallback rather than `undefined` so the empty case parses cleanly:
   * `setupLocalStorageAPI.getData` does `JSON.parse(getItem(key) || JSON.stringify(fallback))`,
   * and `JSON.stringify(undefined)` is `undefined`, which would reach `JSON.parse("undefined")`
   * and depend on the `catch`. The re-check still earns its place — `getData` answers `undefined`
   * when storage is absent or the stored JSON is malformed.
   */
  const readStoredChoices = useCallback((): HeaderChoicesByScope => {
    const stored = storage?.getData({}, isHeaderChoicesByScope);
    return isHeaderChoicesByScope(stored) ? stored : {};
  }, [storage]);

  /**
   * Read-modify-write, so a choice recorded for one scope never drops the other. That also keeps
   * this callback's identity off the Redux record: `persistHeaderChoice` feeds
   * `collapseHeader`/`expandHeader`, and the ~7 pages that collapse on mount and expand on cleanup
   * hold those in their effect deps — a new identity per stored choice would re-fire the pair.
   */
  const writeStoredChoice = useCallback(
    (scope: HeaderScope, choice: UserHeaderChoice) => {
      storage?.updateData({ ...readStoredChoices(), [scope]: choice });
    },
    [storage, readStoredChoices]
  );

  return {
    isStorageReady: storage !== null,
    readStoredChoices,
    writeStoredChoice
  };
}
